import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@root/components/v3/card';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@root/components/v3/tooltip';
import { cn } from '@root/lib/utils';
import { Info } from 'lucide-react';

/**
 * Enhanced Card component that replaces ContentBox
 * @param {Object} props
 * @param {string|React.ReactNode} props.title - Card title
 * @param {string} props.subtitle - Card subtitle/description
 * @param {string} props.infoMsg - Tooltip message for info icon
 * @param {React.ReactNode} props.headerEndElement - Element to display at the end of header (replaces CardAction)
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Inline styles
 * @param {string} props.height - Card height
 * @param {string} props.minHeight - Card minimum height
 * @param {string} props.paddingMode - 'container' or 'content' (if 'content', removes padding from CardContent)
 * @param {string} props.contentClassName - Additional classes for the card content area
 * @param {Function} props.onClick - Click handler
 */
function ContentCard({
  title,
  subtitle,
  infoMsg,
  headerEndElement,
  children,
  className,
  style,
  height,
  minHeight,
  paddingMode = 'container',
  contentClassName,
  onClick,
  ...props
}) {
  return (
    <Card
      {...props}
      className={cn('transition-all duration-300', className)}
      style={{
        ...style,
        ...(height && { height }),
        ...(minHeight && { minHeight }),
      }}
      onClick={onClick}>
      {(title || subtitle || headerEndElement) && (
        <CardHeader className="flex flex-row items-start justify-between space-y-0 p-4 ">
          <div className="flex flex-col space-y-1.5 flex-1">
            {title && (
              <div className="flex items-center gap-2">
                <CardTitle className="text-[14px] font-semibold font-[Manrope] leading-none tracking-tight">
                  {title}
                </CardTitle>
                {infoMsg && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="inline-flex items-center justify-center">
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">{infoMsg}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
          {headerEndElement}
        </CardHeader>
      )}
      <CardContent className={cn(paddingMode === 'content' ? 'p-0' : '', contentClassName)}>
        {children}
      </CardContent>
    </Card>
  );
}

export default ContentCard;
